import type { BreachedCase } from '@compass/client/types/cases_analytics';
import { ArrowDown } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { FormattedDateRelative } from '@/components/FormattedDateRelative';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrgPqcWorkflows } from '@/hooks/useOrgPqcWorkflows';
import { useUsersByIds } from '@/hooks/useUsers';
import { formatCaseId } from '@/utils/cases';
import { buildRoutePath } from '@/utils/routes';

import {
  BREACHED_CASES_PAGE_SIZE,
  useBreachedCaseHydration,
} from '../hooks/useBreachedCaseHydration';

interface BreachedCasesSectionProps {
  organizationId: string;
  /** The response's breached_case_list: created_at DESC, capped server-side. */
  rows: BreachedCase[];
  /** summary.breached_cases.value — the pagination N (same filters, same store). */
  totalBreached: number;
  /** Changes whenever the page filters change; resets the table to its first page. */
  filterKey: string;
  /** The page-level loading gate. */
  loading?: boolean;
}

const MISSING = <span className="text-muted-foreground">—</span>;

/**
 * The SLA breached cases table at the bottom of the AE/PQC Analytics page. The
 * rows come from ClickHouse (ids, created-at, assignee/program/workflow ids);
 * each visible page is hydrated through the case API for the code and completed
 * time, assignee names resolve via LookupUsers and the manufacturer is the PQC
 * workflow's name — the Tier-2 page's pattern. A row the hydration missed still
 * renders from its ClickHouse fields and stays clickable.
 */
export const BreachedCasesSection = React.forwardRef<HTMLElement, BreachedCasesSectionProps>(
  ({ organizationId, rows, totalBreached, filterKey, loading }, ref) => {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    useEffect(() => {
      setPage(0);
    }, [filterKey]);

    const pageRows = useMemo(
      () => rows.slice(page * BREACHED_CASES_PAGE_SIZE, (page + 1) * BREACHED_CASES_PAGE_SIZE),
      [rows, page],
    );
    const { recordById, isLoading: hydrationLoading } = useBreachedCaseHydration(
      organizationId,
      pageRows,
    );

    const assignedIds = useMemo(
      () => [...new Set(pageRows.map(r => r.assigned_user_id).filter((id): id is string => !!id))],
      [pageRows],
    );
    const { usersMap } = useUsersByIds(assignedIds);

    const { pqcWorkflows } = useOrgPqcWorkflows();
    const workflowNameById = useMemo(() => {
      const map = new Map<string, string>();
      for (const w of pqcWorkflows) {
        if (w.id) map.set(w.id, w.name || w.id);
      }
      return map;
    }, [pqcWorkflows]);

    const assigneeCell = (row: BreachedCase) => {
      if (!row.assigned_user_id) return 'Unassigned';
      const user = usersMap.get(row.assigned_user_id);
      return (
        user?.name || user?.email || recordById.get(row.case_record_id ?? '')?.assigned || MISSING
      );
    };

    const showSkeleton = loading || hydrationLoading;
    const first = page * BREACHED_CASES_PAGE_SIZE + 1;
    const last = page * BREACHED_CASES_PAGE_SIZE + pageRows.length;
    const onLastPage = last >= rows.length;
    const truncated = onLastPage && rows.length < totalBreached;

    return (
      <section ref={ref} className="space-y-4 scroll-mt-4">
        <header className="space-y-1">
          <h3 className="text-sm font-semibold leading-5 text-foreground">SLA breached cases</h3>
          <p className="text-xs leading-5 text-muted-foreground">
            Submitted cases that exceeded the 24-business-hour SLA, newest first
          </p>
        </header>

        <Card className="w-full overflow-x-auto p-0 shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="px-4 py-3 font-medium">Case ID</th>
                <th className="px-4 py-3 font-medium">Assignee</th>
                <th className="px-4 py-3 font-medium">Manufacturer</th>
                <th className="px-4 py-3 font-medium">
                  <span className="inline-flex items-center gap-1">
                    Created at <ArrowDown className="h-3 w-3" />
                  </span>
                </th>
                <th className="px-4 py-3 font-medium">Completed at</th>
              </tr>
            </thead>
            <tbody>
              {showSkeleton ? (
                Array.from({ length: pageRows.length || 5 }, (_, i) => (
                  <tr key={i} className="border-b last:border-0">
                    {Array.from({ length: 5 }, (_, c) => (
                      <td key={c} className="px-4 py-3">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : pageRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    No SLA breached cases in this period.
                  </td>
                </tr>
              ) : (
                pageRows.map(row => {
                  const record = recordById.get(row.case_record_id ?? '');
                  const casePath = buildRoutePath(
                    `/cases/${row.case_record_id}`,
                    organizationId,
                    row.program_id ?? '',
                  );
                  const completedAt =
                    record?.workflow_completed_at || record?.workflow_cancelled_at;
                  return (
                    <tr
                      key={row.case_record_id}
                      className="cursor-pointer border-b last:border-0 hover:bg-muted/50"
                      onClick={() => navigate(casePath)}
                    >
                      <td className="px-4 py-3">
                        <Link
                          to={casePath}
                          onClick={e => e.stopPropagation()}
                          className="font-mono text-xs font-medium hover:underline"
                        >
                          {formatCaseId(row.case_record_id, record?.code)}
                        </Link>
                      </td>
                      <td className="px-4 py-3">{assigneeCell(row)}</td>
                      <td className="px-4 py-3">
                        {workflowNameById.get(row.workflow_id ?? '') ?? MISSING}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <FormattedDateRelative value={row.created_at} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {completedAt ? <FormattedDateRelative value={completedAt} /> : MISSING}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {!showSkeleton && pageRows.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2">
              <span className="text-xs text-muted-foreground">
                Showing {first}–{last} of {totalBreached}
                {truncated && (
                  <span className="ml-2">
                    · Showing the newest {rows.length} of {totalBreached} breached cases — narrow
                    the date range to see the rest.
                  </span>
                )}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={onLastPage}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      </section>
    );
  },
);

BreachedCasesSection.displayName = 'BreachedCasesSection';
